import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitKycRequest } from '../../../../api/kyc';
import { Button } from '../../../../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select';
import { Label } from '../../../../components/ui/label';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { X, FileText, AlertCircle } from 'lucide-react';

const KycUploadForm = () => {
    const { t } = useTranslation('profile');
    const queryClient = useQueryClient();
    const { handleSubmit, control, reset } = useForm();
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState([]);

    const mutation = useMutation({
        mutationFn: submitKycRequest,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kycStatus'] });
            reset();
            setFiles([]);
            setErrors([]);
            alert(t('kycDocumentsSubmittedSuccess'));
        },
        onError: error => {
            console.error('KYC submission error:', error);

            let errorMessage = t('submissionFailedTryAgain');
            if (error.response?.data?.errors) {
                const errorMessages = [];
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    if (Array.isArray(messages)) {
                        errorMessages.push(...messages);
                    } else {
                        errorMessages.push(messages);
                    }
                });
                setErrors(errorMessages);
                return;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setErrors([errorMessage]);
        },
    });

    const validateFile = file => {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        const maxSize = 4 * 1024 * 1024; // 4MB

        if (!allowedTypes.includes(file.type)) {
            return t('fileMustBeJpgPngPdf', { name: file.name });
        }

        if (file.size > maxSize) {
            return t('fileTooLargeMax4mb', { name: file.name });
        }

        return null;
    };

    const handleFileChange = e => {
        const selectedFiles = Array.from(e.target.files);
        const newErrors = [];
        const validFiles = [];

        selectedFiles.forEach(file => {
            const error = validateFile(file);
            if (error) {
                newErrors.push(error);
            } else {
                validFiles.push({ file, type: 'passport' });
            }
        });

        setErrors(newErrors);
        setFiles(validFiles);
    };

    const removeFile = index => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const onSubmit = data => {
        if (files.length === 0) {
            setErrors([t('selectAtLeastOneDocument')]);
            return;
        }

        setErrors([]);
        const formData = new FormData();
        files.forEach((file, index) => {
            formData.append(`documents[${index}][file]`, file.file);
            formData.append(`documents[${index}][type]`, data[`document_type_${index}`]);
        });
        mutation.mutate(formData);
    };

    return (
        <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">{t('submitKycDocuments')}</h3>

            {/* Error Messages */}
            {errors.length > 0 && (
                <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <ul className="list-disc list-inside space-y-1">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                    <Button variant="outline" asChild>
                        <Label htmlFor="file-upload" className="cursor-pointer">
                            {t('selectDocuments')}
                            <input
                                id="file-upload"
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png,.pdf"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </Label>
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">{t('acceptedFormats')}</p>
                </div>

                {files.map((file, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 border rounded-lg">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <p className="flex-1 text-sm">{file.file.name}</p>
                        <div className="w-40">
                            <Controller
                                name={`document_type_${index}`}
                                control={control}
                                defaultValue="passport"
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('documentType')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="passport">
                                                {t('passport')}
                                            </SelectItem>
                                            <SelectItem value="drivers_license">
                                                {t('driversLicense')}
                                            </SelectItem>
                                            <SelectItem value="utility_bill">
                                                {t('utilityBill')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="text-destructive hover:text-destructive"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ))}

                <div>
                    <Button
                        type="submit"
                        disabled={mutation.isLoading || files.length === 0}
                        className="w-full"
                    >
                        {mutation.isLoading ? t('submitting') : t('submitDocuments')}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default KycUploadForm;
